import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import type { ColorMode } from './tokens.js';

interface ColorModeContextValue {
  mode: ColorMode;
  /** User explicitly picked a mode, vs following system. */
  userPreference: ColorMode | 'system';
  setMode: (next: ColorMode | 'system') => void;
  toggleMode: () => void;
}

const ColorModeContext = createContext<ColorModeContextValue | null>(null);

const STORAGE_KEY = 'fse_color_mode';

function readSystemPreference(): ColorMode {
  if (typeof window === 'undefined' || !window.matchMedia) return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function readStoredPreference(): ColorMode | 'system' {
  if (typeof window === 'undefined') return 'system';
  const raw = window.localStorage.getItem(STORAGE_KEY);
  if (raw === 'light' || raw === 'dark' || raw === 'system') return raw;
  return 'system';
}

export function ColorModeProvider({ children }: { children: ReactNode }) {
  const [userPreference, setUserPreferenceState] = useState<ColorMode | 'system'>(() =>
    readStoredPreference(),
  );
  const [systemMode, setSystemMode] = useState<ColorMode>(() => readSystemPreference());

  // Listen for OS-level color-scheme changes so 'system' mode tracks live.
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent) => setSystemMode(e.matches ? 'dark' : 'light');
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const setMode = useCallback((next: ColorMode | 'system') => {
    setUserPreferenceState(next);
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(STORAGE_KEY, next);
    }
  }, []);

  const mode: ColorMode = userPreference === 'system' ? systemMode : userPreference;

  const toggleMode = useCallback(() => {
    setMode(mode === 'dark' ? 'light' : 'dark');
  }, [mode, setMode]);

  const value = useMemo<ColorModeContextValue>(
    () => ({ mode, userPreference, setMode, toggleMode }),
    [mode, userPreference, setMode, toggleMode],
  );

  return <ColorModeContext.Provider value={value}>{children}</ColorModeContext.Provider>;
}

export function useColorMode(): ColorModeContextValue {
  const ctx = useContext(ColorModeContext);
  if (!ctx) throw new Error('useColorMode must be used inside <FseThemeProvider>');
  return ctx;
}
